import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, 
  Plus, 
  Upload, 
  Download,
  Building2,
  FileText,
  CheckCircle,
  AlertCircle
} from 'lucide-react'
import { motion } from 'framer-motion'
import { portfolioTrackerService } from '@/services/portfolioTrackerService'
import { PageTemplate } from '@/components/templates'
import { Button, Input } from '@/components/atoms'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CONTAINER_SIZES } from '@/constants'

const PortfolioCreateScreen = () => {
  const navigate = useNavigate()
  
  const [activeTab, setActiveTab] = useState<'manual' | 'csv'>('manual')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // Manual creation form
  const [portfolioName, setPortfolioName] = useState('')
  const [portfolioDescription, setPortfolioDescription] = useState('')

  // CSV import form
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [csvPortfolioName, setCsvPortfolioName] = useState('')
  const [csvPreview, setCsvPreview] = useState<any[]>([])
  const [csvValidation, setCsvValidation] = useState<{
    isValid: boolean
    errors: string[]
    warnings: string[]
  }>({ isValid: false, errors: [], warnings: [] })

  const handleManualCreate = async () => {
    if (!portfolioName.trim()) {
      setError('Portfolio name is required')
      return
    }

    try {
      setLoading(true)
      setError(null)
      
      const newPortfolio = await portfolioTrackerService.createPortfolio(portfolioName.trim())
      setSuccessMessage('Portfolio created successfully!')
      
      setTimeout(() => {
        navigate(`/portfolio/${newPortfolio.portfolio_id}`)
      }, 1500)
    } catch (err) {
      setError('Failed to create portfolio')
      console.error('Error creating portfolio:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleCSVImport = async () => {
    if (!csvFile || !csvPortfolioName.trim()) {
      setError('Please select a CSV file and enter portfolio name')
      return
    }

    if (!csvValidation.isValid) {
      setError('Please fix CSV validation errors before importing')
      return
    }

    try {
      setLoading(true)
      setError(null)
      
      const result = await portfolioTrackerService.createPortfolioFromCSV(csvPortfolioName.trim(), csvFile)
      setSuccessMessage(`Portfolio created with ${result.matched_assets} assets!`)
      
      setTimeout(() => {
        navigate(`/portfolio/${result.portfolio_id}`)
      }, 1500)
    } catch (err: any) {
      if (portfolioTrackerService.isCSVError(err)) {
        setError(`CSV Error: ${err.error}`)
      } else {
        setError('Failed to import CSV')
      }
      console.error('Error importing CSV:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file && file.type === 'text/csv') {
      setCsvFile(file)
      setError(null)
      validateCSV(file)
    } else {
      setError('Please select a valid CSV file')
      setCsvFile(null)
      setCsvPreview([])
      setCsvValidation({ isValid: false, errors: [], warnings: [] })
    }
  }

  const validateCSV = (file: File) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const text = e.target?.result as string
      const lines = text.split('\n')
      const headers = lines[0]?.split(',').map(h => h.trim().toLowerCase())
      
      const requiredColumns = ['project name', 'date of investment', 'invested amount']
      const optionalColumns = ['project website', 'project valuation', 'amount of equity or tokens', 'terms']
      
      const errors: string[] = []
      const warnings: string[] = []
      
      // Check required columns
      const missingColumns = requiredColumns.filter(col => !headers?.includes(col))
      if (missingColumns.length > 0) {
        errors.push(`Missing required columns: ${missingColumns.join(', ')}`)
      }
      
      // Check for unknown columns
      const allValidColumns = [...requiredColumns, ...optionalColumns]
      const unknownColumns = headers?.filter(col => !allValidColumns.includes(col)) || []
      if (unknownColumns.length > 0) {
        warnings.push(`Unknown columns will be ignored: ${unknownColumns.join(', ')}`)
      }
      
      // Parse preview data
      const previewData = lines.slice(1, 6).map(line => {
        const values = line.split(',')
        const row: any = {}
        headers?.forEach((header, index) => {
          row[header] = values[index]?.trim() || ''
        })
        return row
      }).filter(row => Object.values(row).some(val => val !== ''))
      
      setCsvPreview(previewData)
      setCsvValidation({
        isValid: errors.length === 0,
        errors,
        warnings
      })
    }
    reader.readAsText(file)
  }

  const downloadCSVTemplate = () => {
    const template = `Project Name,Date of Investment,Invested Amount,Project Website,Project Valuation,Amount of equity or tokens,Terms
Example Project Alpha,2023-01-15,50000,https://example-alpha.com,1000000,100000,SAFE agreement
Example Project Beta,2023-02-20,75000,https://example-beta.com,1500000,150000,Equity investment
Example Project Gamma,2023-03-10,25000,https://example-gamma.com,500000,50000,Token warrant`
    
    const blob = new Blob([template], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'portfolio_template.csv'
    a.click()
    window.URL.revokeObjectURL(url)
  }

  return (
    <PageTemplate
      title="Create Portfolio"
      subtitle="Create a new portfolio to track your investments"
      maxWidth={CONTAINER_SIZES.WIDE}
    >
      {/* Success Message */}
      {successMessage && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-500/20 border border-green-500 text-green-400 px-4 py-3 rounded-lg"
        >
          {successMessage}
        </motion.div>
      )}

      {/* Error Message */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-500/20 border border-green-500 text-green-400 px-4 py-3 rounded-lg"
        >
          {error}
        </motion.div>
      )}

      {/* Header Actions */}
      <PageTemplate.Section>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            leftIcon={<ArrowLeft size={16} />}
            onClick={() => navigate('/portfolio')}
          >
            Back to Portfolios
          </Button>
        </div>
      </PageTemplate.Section>

      {/* Tab Navigation */}
      <PageTemplate.Section>
        <div className="flex gap-3">
          <Button
            variant={activeTab === 'manual' ? 'primary' : 'outline'}
            leftIcon={<Plus size={16} />}
            onClick={() => setActiveTab('manual')}
          >
            Manual Creation
          </Button>
          <Button
            variant={activeTab === 'csv' ? 'primary' : 'outline'}
            leftIcon={<Upload size={16} />}
            onClick={() => setActiveTab('csv')}
          >
            Import CSV
          </Button>
          <Button
            variant="outline"
            leftIcon={<Download size={16} />}
            onClick={downloadCSVTemplate}
          >
            Download Template
          </Button>
        </div>
      </PageTemplate.Section>

      {/* Manual Creation Tab */}
      {activeTab === 'manual' && (
        <PageTemplate.Section>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card variant="elevated">
              <CardHeader>
                <CardTitle>Create Portfolio Manually</CardTitle>
                <p className="text-muted-foreground">
                  Create an empty portfolio and add assets later
                </p>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Portfolio Name *</label>
                    <Input
                      value={portfolioName}
                      onChange={(e) => setPortfolioName(e.target.value)}
                      placeholder="Enter portfolio name"
                      onKeyPress={(e) => e.key === 'Enter' && handleManualCreate()}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Description (Optional)</label>
                    <textarea
                      value={portfolioDescription}
                      onChange={(e) => setPortfolioDescription(e.target.value)}
                      placeholder="Add a description for your portfolio"
                      className="w-full p-3 border border-input rounded-md resize-none h-24"
                    />
                  </div>

                  <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Building2 className="text-blue-500 mt-1" size={20} />
                      <div>
                        <h4 className="font-medium text-blue-900 dark:text-blue-100">What's Next?</h4>
                        <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                          After creating your portfolio, you can add assets manually or import them from a CSV file.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => navigate('/portfolio')}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleManualCreate}
                      disabled={loading || !portfolioName.trim()}
                      className="flex-1"
                    >
                      {loading ? 'Creating...' : 'Create Portfolio'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </PageTemplate.Section>
      )}

      {/* CSV Import Tab */}
      {activeTab === 'csv' && (
        <PageTemplate.Section>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card variant="elevated">
              <CardHeader>
                <CardTitle>Import Portfolio from CSV</CardTitle>
                <p className="text-muted-foreground">
                  Create a portfolio with assets from a CSV file
                </p>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Portfolio Name *</label>
                    <Input
                      value={csvPortfolioName}
                      onChange={(e) => setCsvPortfolioName(e.target.value)}
                      placeholder="Enter portfolio name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">CSV File *</label>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileChange}
                      className="w-full p-3 border border-input rounded-md"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Required columns: Project Name, Date of Investment, Invested Amount
                    </p>
                  </div>

                  {/* CSV Validation */}
                  {csvValidation.errors.length > 0 && (
                    <div className="bg-red-50 dark:bg-red-950 p-4 rounded-lg">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="text-red-500 mt-1" size={20} />
                        <div>
                          <h4 className="font-medium text-red-900 dark:text-red-100">Validation Errors</h4>
                          <ul className="text-sm text-red-700 dark:text-red-300 mt-1 space-y-1">
                            {csvValidation.errors.map((error, index) => (
                              <li key={index}>• {error}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {csvValidation.warnings.length > 0 && (
                    <div className="bg-yellow-50 dark:bg-yellow-950 p-4 rounded-lg">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="text-yellow-500 mt-1" size={20} />
                        <div>
                          <h4 className="font-medium text-yellow-900 dark:text-yellow-100">Warnings</h4>
                          <ul className="text-sm text-yellow-700 dark:text-yellow-300 mt-1 space-y-1">
                            {csvValidation.warnings.map((warning, index) => (
                              <li key={index}>• {warning}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* CSV Preview */}
                  {csvPreview.length > 0 && csvValidation.isValid && (
                    <div>
                      <h4 className="font-medium mb-3">CSV Preview</h4>
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 dark:bg-gray-800 p-3 border-b border-gray-200 dark:border-gray-700">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="text-green-500" size={16} />
                            <span className="text-sm font-medium text-green-700 dark:text-green-300">
                              CSV file is valid
                            </span>
                          </div>
                        </div>
                        <div className="max-h-60 overflow-y-auto">
                          <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-gray-800">
                              <tr>
                                {Object.keys(csvPreview[0] || {}).map((header) => (
                                  <th key={header} className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                                    {header}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {csvPreview.map((row, index) => (
                                <tr key={index} className="border-b border-gray-100 dark:border-gray-700">
                                  {Object.values(row).map((value, cellIndex) => (
                                    <td key={cellIndex} className="px-3 py-2 text-sm">
                                      {String(value)}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg">
                    <div className="flex items-start gap-3">
                      <FileText className="text-blue-500 mt-1" size={20} />
                      <div>
                        <h4 className="font-medium text-blue-900 dark:text-blue-100">CSV Format</h4>
                        <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                          Your CSV should include: Project Name, Date of Investment, Invested Amount. 
                          Optional columns: Project Website, Project Valuation, Amount of equity or tokens, Terms.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => navigate('/portfolio')}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleCSVImport}
                      disabled={loading || !csvFile || !csvPortfolioName.trim() || !csvValidation.isValid}
                      className="flex-1"
                    >
                      {loading ? 'Importing...' : 'Import Portfolio'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </PageTemplate.Section>
      )}
    </PageTemplate>
  )
}

export default PortfolioCreateScreen
